import { isAxiosError } from 'axios';
import { posClient } from './client';
import {
  normalizeDaySession,
  normalizeSessionListRow,
  wireDaySessionSchema,
  wireSessionEnvelopeSchema,
  wireSessionsIndexSchema,
  wireSessionSummaryEnvelopeSchema,
  type DaySession,
  type DaySessionList,
  type DaySessionSummary,
} from '@/types/sessions';

export interface SessionRequestOptions {
  signal?: AbortSignal;
}

export interface StartSessionBody {
  opening_cash?: string;
  operation_id?: string;
  terminal_id: number;
  branch_id?: number;
  notes?: string;
}

export interface StartSessionResult {
  session: DaySession;
  /**
   * True when the terminal already had an open session and we adopted it.
   * The 400 "Terminal already has an open day session" response returns the
   * EXISTING session in `data` (DaySessionController.php:43-49) — recover by
   * using it instead of surfacing an error.
   */
  adopted: boolean;
}

export interface SessionListParams {
  page?: number;
  per_page?: number;
  from_date?: string;
  to_date?: string;
  terminal_id?: number;
  branch_id?: number;
  status?: 'open' | 'closed';
}

export const sessionsApi = {
  /**
   * GET {pos}/day-sessions/current?terminal_id=N — always 200; `data: null`
   * means "day not started" (show the Start Day screen), not an error.
   */
  async getCurrent(
    terminalId: number,
    options: SessionRequestOptions = {},
  ): Promise<DaySession | null> {
    const res = await posClient.get('/day-sessions/current', {
      params: { terminal_id: terminalId },
      signal: options.signal,
    });
    const body = wireSessionEnvelopeSchema.parse(res.data);
    return body.data ? normalizeDaySession(body.data) : null;
  },

  /**
   * POST {pos}/day-sessions/start — 201 with the new session; 400
   * "already has an open day session" is ADOPTED, not thrown (see
   * StartSessionResult.adopted). Other 400/422s throw normally.
   *
   * Gotchas (code-verified): the BranchScope creating-hook overwrites body
   * branch_id with the X-Branch header when present — callers must keep them
   * consistent; the "already open" check is itself branch-scoped, so an open
   * session under a different X-Branch is not detected.
   */
  async start(
    body: StartSessionBody,
    options: SessionRequestOptions = {},
  ): Promise<StartSessionResult> {
    try {
      const res = await posClient.post('/day-sessions/start', body, { signal: options.signal });
      const envelope = wireSessionEnvelopeSchema.safeParse(res.data);
      if (envelope.success && envelope.data.data) {
        return { session: normalizeDaySession(envelope.data.data), adopted: false };
      }
      /*
        The day IS open — the 201 body just could not be read.

        POST /day-sessions/start returns the in-memory model rather than the
        stored row, so the session it echoes back has NO `id` and carries
        `branch_id` as the STRING the X-Branch header supplied
        (DaySessionController.php:52-68; the BranchScope creating-hook is what
        rewrites branch_id). The schema requires a numeric id, so parsing threw,
        and the register reported "Unknown error" over a day that had just been
        opened successfully — the operator then had no way to sell and no
        indication that retrying was pointless.

        GET /day-sessions/current returns the stored row and parses cleanly, so
        it is asked instead of guessing at the malformed body. Only if THAT
        comes back empty is the start genuinely considered failed.
      */
      const current = await sessionsApi.getCurrent(body.terminal_id, { signal: options.signal });
      if (current) return { session: current, adopted: true };
      throw new Error('day-sessions/start returned no session');
    } catch (e) {
      // Adopt-on-400: the contract is the SHAPE (400 + success:false + a
      // parseable session in data), not the message text.
      if (isAxiosError(e) && [400, 409].includes(e.response?.status ?? 0)) {
        const data = (e.response?.data as { data?: unknown } | undefined)?.data;
        const existing = wireDaySessionSchema.safeParse(data);
        if (existing.success) {
          return { session: normalizeDaySession(existing.data), adopted: true };
        }
      }
      throw e;
    }
  },

  /**
   * POST {pos}/day-sessions/{id}/end — 200 closed session; 400 already
   * closed; 404 {success:false,message}; omitting notes keeps existing notes.
   */
  async end(id: number, notes?: string, options: SessionRequestOptions = {}): Promise<DaySession> {
    const res = await posClient.post(
      `/day-sessions/${id}/end`,
      notes === undefined ? {} : { notes },
      { signal: options.signal },
    );
    const body = wireSessionEnvelopeSchema.parse(res.data);
    if (!body.data) throw new Error(`day-sessions/${id}/end returned no session`);
    return normalizeDaySession(body.data);
  },

  /** GET {pos}/day-sessions/{id}/summary — Z-report; plain numbers, not strings. */
  async summary(id: number, options: SessionRequestOptions = {}): Promise<DaySessionSummary> {
    const res = await posClient.get(`/day-sessions/${id}/summary`, {
      signal: options.signal,
      // Manager history may include retired/other tills; keep the verified branch scope.
      skipTerminalHeader: true,
    });
    return wireSessionSummaryEnvelopeSchema.parse(res.data).data;
  },

  /**
   * GET {pos}/day-sessions — history index with flattened pagination.
   *
   * Uses the documented skipBranchHeader ESCAPE HATCH (src/api/client.ts):
   * sending X-Branch 500s this endpoint (BranchScope's unqualified branch_id
   * collides with the orders leftJoin). Branch filtering goes through the
   * VALIDATED branch_id query param instead — callers wanting branch scope
   * must pass params.branch_id explicitly.
   */
  async list(
    params: SessionListParams = {},
    options: SessionRequestOptions = {},
  ): Promise<DaySessionList> {
    const res = await posClient.get('/day-sessions', {
      params,
      signal: options.signal,
      skipBranchHeader: true,
    });
    const body = wireSessionsIndexSchema.parse(res.data);
    return {
      sessions: body.data.map(normalizeSessionListRow),
      total: body.total,
      perPage: body.per_page,
      currentPage: body.current_page,
      lastPage: body.last_page,
    };
  },
};
